'use client';

import { useState, useCallback, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Trash2, Check, PenLine, AlertCircle } from 'lucide-react';
import { Z } from '@/lib/z-index';
import { useAnimatedClose } from '@/lib/use-animated-close';
import { COMMUNITY_BOARDS } from '@/lib/community/boards';
import { updatePost, deletePost } from '@/lib/community/community-api';
import { publishPost } from '@/lib/community/service/interaction-service';
import { uploadFile } from '@/lib/api/upload';
import { useImageCompress } from '@/lib/hooks/use-image-compress';
import { useToast } from '@/lib/hooks/use-toast';
import type { PublishDraft, AbilityType, CommunityPost } from '@/lib/community/types';
import { CoverCropModal } from './cover-crop-modal';
import { PublishDetailPreview } from './publish-detail-preview';
import { CompressProgressModal } from './compress-progress-modal';
import { PublishFormPanel } from './publish-form-panel';
import { useAuthContext } from '@/lib/auth-context';
import { analytics } from '@/src/utils/analytics';

/* ── Mock trending tags ── */
const MOCK_TRENDING_TAGS = [
	'量化策略',
	'AI写作',
	'代码审查',
	'自动化办公',
	'GPT技巧',
	'Agent实战',
	'数据分析',
	'效率工具',
	'Prompt工程',
	'多Agent协作',
	'知识管理',
	'副业赚钱',
];

/* ── Mock hashtag suggestions for # autocomplete ── */
const MOCK_HASHTAG_SUGGESTIONS = [
	'量化策略',
	'AI写作',
	'代码审查',
	'自动化办公',
	'GPT技巧',
	'Agent实战',
	'数据分析',
	'效率工具',
	'Prompt工程',
	'多Agent协作',
	'知识管理',
	'副业赚钱',
	'职场提效',
	'创意设计',
	'学习方法',
	'投资理财',
];

const HASHTAG_REGEX = /#([一-鿿A-Za-z0-9_]+)/g;
const HASH_PARTIAL_REGEX = /#([一-鿿A-Za-z0-9_]*)$/;
const HASH_WHOLE_TAG_REGEX = /#([一-鿿A-Za-z0-9_]+)\s?$/;

/** Accepted cover image types */
const COVER_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
/** Max cover image size: 32 MB */
const COVER_MAX_SIZE = 32 * 1024 * 1024;

/* ── Draft persistence ── */
const DRAFT_KEY = 'safeclaw:draft:publish';
interface DraftData {
	title: string;
	description: string;
	coverPreview: string | null;
	boardSlug: string;
}

export interface PublishModalInitialDraft {
	title?: string;
	description?: string;
	boardSlug?: string;
	coverFile?: File | null;
	coverPreview?: string | null;
	aiMessage?: string;
	skillIds?: number[];
}

async function toBase64(url: string): Promise<string> {
	if (url.startsWith('data:')) return url;
	const res = await fetch(url);
	const blob = await res.blob();
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

async function saveDraft(draft: DraftData) {
	try {
		const coverBase64 = draft.coverPreview ? await toBase64(draft.coverPreview) : null;
		localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, coverPreview: coverBase64 }));
	} catch { }
}
function loadDraft(): DraftData | null {
	try {
		const raw = localStorage.getItem(DRAFT_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}
function clearDraft() {
	try {
		localStorage.removeItem(DRAFT_KEY);
	} catch { }
}

/**
 * PublishModal -- Redesigned post creation modal.
 *
 * Layout: two separate glass panels side by side --
 * left is the live preview card, right is the form card (header + body + footer).
 */
export function PublishModal({
	open,
	onClose,
	editPost,
	initialDraft,
	onSuccess,
	onDeleted,
}: {
	open: boolean;
	onClose: () => void;
	editPost?: CommunityPost;
	initialDraft?: PublishModalInitialDraft | null;
	onSuccess?: () => void;
	onDeleted?: () => void;
}) {
	const isEditMode = !!editPost;
	const { closing, requestClose, reset } = useAnimatedClose(onClose, 280);
	const [entered, setEntered] = useState(false);
	const toast = useToast();
	const { compress, cancel: cancelCompress, compressing, progress: compressProgress } = useImageCompress();
	const { currentUser } = useAuthContext();

	/* ── Delete confirmation state ── */
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleting, setDeleting] = useState(false);

	/* ── Exit confirmation state ── */
	const [showExitConfirm, setShowExitConfirm] = useState(false);

	/* ── Board picker modal ── */
	const [showBoardPicker, setShowBoardPicker] = useState(false);
	const [pickerSelectedBoard, setPickerSelectedBoard] = useState('');

	const [title, setTitle] = useState(editPost?.title ?? '');
	const [description, setDescription] = useState(editPost?.description ?? '');
	const [boardSlug, setBoardSlug] = useState(editPost?.boardSlug ?? '');
	const [aiMessage, setAiMessage] = useState(initialDraft?.aiMessage);
	const [skillIds, setSkillIds] = useState<number[]>(initialDraft?.skillIds ?? []);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [submitted, setSubmitted] = useState(false);
	const [submittedPostId, setSubmittedPostId] = useState<string | null>(null);

	/* ── Cover image state ── */
	const [coverFile, setCoverFile] = useState<File | null>(null);
	const [coverPreview, setCoverPreview] = useState<string | null>(editPost?.coverImage ?? null);
	const [coverError, setCoverError] = useState('');
	const [coverShrinking, setCoverShrinking] = useState(false);
	const coverInputRef = useRef<HTMLInputElement>(null);
	/** Raw image URL shown in the crop modal; null means crop modal is closed */
	const [rawCoverSrc, setRawCoverSrc] = useState<string | null>(null);

	/* ── # autocomplete state ── */
	const [showHashtagMenu, setShowHashtagMenu] = useState(false);
	const [hashtagQuery, setHashtagQuery] = useState('');
	const [hashtagMenuIndex, setHashtagMenuIndex] = useState(0);
	const [hashtagMenuPosition, setHashtagMenuPosition] = useState({ top: 0, left: 0 });
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const hashtagMenuRef = useRef<HTMLDivElement>(null);
	const hashtagInsertPosRef = useRef<number>(-1);

	/* ── Tags more popup state ── */
	const [showAllTags, setShowAllTags] = useState(false);
	const [morePopupPos, setMorePopupPos] = useState<{ top: number; left: number } | null>(null);
	const tagsMoreRef = useRef<HTMLDivElement>(null);
	const morePopupRef = useRef<HTMLDivElement>(null);

	/* ── Entrance animation ── */
	useEffect(() => {
		if (!open) return;
		const raf = requestAnimationFrame(() => setEntered(true));
		return () => cancelAnimationFrame(raf);
	}, [open]);

	/* ── Lock body scroll ── */
	useEffect(() => {
		if (!open) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = prev;
		};
	}, [open]);

	/* ── Reset on close / load draft on open ── */
	useEffect(() => {
		if (!open) {
			setEntered(false);
			setSubmitted(false);
			setSubmittedPostId(null);
			setShowBoardPicker(false);
			setShowExitConfirm(false);
			setShowAllTags(false);
			setMorePopupPos(null);
			reset();
			setTitle(editPost?.title ?? '');
			setDescription(editPost?.description ?? '');
			setBoardSlug(editPost?.boardSlug ?? '');
			setAiMessage(undefined);
			setSkillIds([]);
			if (coverPreview && coverPreview !== editPost?.coverImage) URL.revokeObjectURL(coverPreview);
			if (rawCoverSrc) URL.revokeObjectURL(rawCoverSrc);
			setCoverFile(null);
			setCoverPreview(editPost?.coverImage ?? null);
			setRawCoverSrc(null);
			setCoverError('');
		} else {
			setTitle(editPost?.title ?? initialDraft?.title ?? '');
			setDescription(editPost?.description ?? initialDraft?.description ?? '');
			setBoardSlug(editPost?.boardSlug ?? initialDraft?.boardSlug ?? '');
			setAiMessage(initialDraft?.aiMessage);
			setSkillIds(initialDraft?.skillIds ?? []);
			setCoverPreview(editPost?.coverImage ?? initialDraft?.coverPreview ?? null);
			setCoverFile(initialDraft?.coverFile ?? null);

			if (!isEditMode && !initialDraft) {
				const draft = loadDraft();
				if (draft) {
					setTitle(draft.title);
					setDescription(draft.description);
					setBoardSlug(draft.boardSlug);
					if (draft.coverPreview) {
						setCoverPreview(draft.coverPreview);
					}
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, reset, editPost, initialDraft]);

	/* ── Cover image handlers ── */
	const validateCoverFile = useCallback((f: File): string | null => {
		if (!COVER_ACCEPTED_TYPES.includes(f.type)) return '仅支持 JPG、PNG、WebP、GIF 格式';
		if (f.size > COVER_MAX_SIZE) return '图片大小不能超过 32MB';
		return null;
	}, []);

	const handleCoverSelect = useCallback(
		async (f: File) => {
			const err = validateCoverFile(f);
			if (err) {
				setCoverError(err);
				return;
			}
			setCoverError('');

			// 大图先压缩再进裁剪器
			const compressed = await compress(f);
			if (!compressed) {
				toast.info('已取消图片压缩');
				return;
			}

			// Open crop modal with the compressed image
			if (rawCoverSrc) URL.revokeObjectURL(rawCoverSrc);
			setRawCoverSrc(URL.createObjectURL(compressed));
		},
		[rawCoverSrc, validateCoverFile, compress, toast],
	);

	const handleCoverInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const f = e.target.files?.[0];
			if (f) handleCoverSelect(f);
			e.target.value = '';
		},
		[handleCoverSelect],
	);

	/** Called when user confirms the crop */
	const handleCropConfirm = useCallback(
		(croppedFile: File, previewUrl: string) => {
			if (coverPreview && coverPreview !== editPost?.coverImage) URL.revokeObjectURL(coverPreview);
			setCoverFile(croppedFile);
			setCoverPreview(previewUrl);
			if (rawCoverSrc) URL.revokeObjectURL(rawCoverSrc);
			setRawCoverSrc(null);
		},
		[coverPreview, rawCoverSrc, editPost?.coverImage],
	);

	/** Called when user cancels the crop */
	const handleCropCancel = useCallback(() => {
		if (rawCoverSrc) URL.revokeObjectURL(rawCoverSrc);
		setRawCoverSrc(null);
	}, [rawCoverSrc]);

	const handleCoverRemove = useCallback(() => {
		setCoverShrinking(true);
		setTimeout(() => {
			if (coverPreview) URL.revokeObjectURL(coverPreview);
			if (rawCoverSrc) URL.revokeObjectURL(rawCoverSrc);
			setCoverFile(null);
			setCoverPreview(null);
			setRawCoverSrc(null);
			setCoverError('');
			setCoverShrinking(false);
		}, 300);
	}, [coverPreview, rawCoverSrc]);

	/* ── Filter hashtag suggestions ── */
	const filteredSuggestions = useMemo(() => {
		if (!hashtagQuery) return MOCK_HASHTAG_SUGGESTIONS.slice(0, 8);
		const q = hashtagQuery.toLowerCase();
		return MOCK_HASHTAG_SUGGESTIONS.filter((t) => t.toLowerCase().includes(q)).slice(0, 8);
	}, [hashtagQuery]);

	/* ── Compute hashtag dropdown position from caret ── */
	const updateHashtagMenuPosition = useCallback(() => {
		const textarea = textareaRef.current;
		if (!textarea) return;

		const mirror = document.createElement('div');
		const computed = window.getComputedStyle(textarea);
		const props = [
			'font-family',
			'font-size',
			'font-weight',
			'line-height',
			'letter-spacing',
			'word-spacing',
			'text-indent',
			'padding-top',
			'padding-right',
			'padding-bottom',
			'padding-left',
			'border-top-width',
			'border-right-width',
			'border-bottom-width',
			'border-left-width',
			'box-sizing',
			'white-space',
			'word-wrap',
			'overflow-wrap',
			'width',
		];
		mirror.style.position = 'absolute';
		mirror.style.visibility = 'hidden';
		mirror.style.whiteSpace = 'pre-wrap';
		mirror.style.wordWrap = 'break-word';
		for (const p of props) {
			mirror.style.setProperty(p, computed.getPropertyValue(p));
		}

		const textBeforeCaret = textarea.value.slice(0, textarea.selectionStart);
		const textNode = document.createTextNode(textBeforeCaret);
		const caretSpan = document.createElement('span');
		caretSpan.textContent = '|';
		mirror.appendChild(textNode);
		mirror.appendChild(caretSpan);
		document.body.appendChild(mirror);

		const _textareaRect = textarea.getBoundingClientRect();
		const caretRect = caretSpan.getBoundingClientRect();
		const mirrorRect = mirror.getBoundingClientRect();

		const relativeTop = caretRect.top - mirrorRect.top;
		const relativeLeft = caretRect.left - mirrorRect.left;

		setHashtagMenuPosition({
			top: relativeTop + textarea.offsetTop - textarea.scrollTop + 24,
			left: relativeLeft + textarea.offsetLeft + 4,
		});

		document.body.removeChild(mirror);
	}, []);

	/* ── Handle textarea input for # detection ── */
	const handleDescriptionChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const value = e.target.value;
			const cursorPos = e.target.selectionStart;
			setDescription(value);

			const textBeforeCursor = value.slice(0, cursorPos);
			const hashMatch = textBeforeCursor.match(HASH_PARTIAL_REGEX);

			if (hashMatch) {
				setHashtagQuery(hashMatch[1]);
				setHashtagMenuIndex(0);
				hashtagInsertPosRef.current = cursorPos - hashMatch[0].length;
				setShowHashtagMenu(true);
				requestAnimationFrame(() => updateHashtagMenuPosition());
			} else {
				setShowHashtagMenu(false);
				setHashtagQuery('');
			}
		},
		[updateHashtagMenuPosition],
	);

	/* ── Insert selected hashtag ── */
	const insertHashtag = useCallback(
		(tag: string) => {
			const textarea = textareaRef.current;
			if (!textarea) return;

			const insertPos = hashtagInsertPosRef.current;
			if (insertPos < 0) return;

			const before = description.slice(0, insertPos);
			const after = description.slice(textarea.selectionStart);
			const newText = `${before}#${tag} ${after}`;

			setDescription(newText);
			setShowHashtagMenu(false);
			setHashtagQuery('');

			requestAnimationFrame(() => {
				const newCursorPos = insertPos + tag.length + 2;
				textarea.focus();
				textarea.setSelectionRange(newCursorPos, newCursorPos);
			});
		},
		[description],
	);

	/* ── Handle keyboard navigation in hashtag menu ── */
	const handleTextareaKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			const textarea = textareaRef.current;

			// Backspace delete whole tag
			if (e.key === 'Backspace' && textarea) {
				const pos = textarea.selectionStart;
				const textBefore = description.slice(0, pos);
				const tagMatch = textBefore.match(HASH_WHOLE_TAG_REGEX);
				if (tagMatch) {
					e.preventDefault();
					const tagStart = pos - tagMatch[0].length;
					setDescription(description.slice(0, tagStart) + description.slice(pos));
					requestAnimationFrame(() => {
						textarea.setSelectionRange(tagStart, tagStart);
					});
					return;
				}
			}

			if (!showHashtagMenu || filteredSuggestions.length === 0) return;

			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setHashtagMenuIndex((i) => (i + 1) % filteredSuggestions.length);
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				setHashtagMenuIndex((i) => (i - 1 + filteredSuggestions.length) % filteredSuggestions.length);
			} else if (e.key === 'Enter') {
				e.preventDefault();
				insertHashtag(filteredSuggestions[hashtagMenuIndex]);
			} else if (e.key === 'Escape') {
				setShowHashtagMenu(false);
			}
		},
		[showHashtagMenu, filteredSuggestions, hashtagMenuIndex, insertHashtag, description],
	);

	/* ── Insert a trending tag ── */
	const handleTrendingTagClick = useCallback(
		(tag: string) => {
			const textarea = textareaRef.current;
			if (description.includes(`#${tag}`)) {
				// Toggle: remove tag
				const newText = description.replace(new RegExp(`#${tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s?`, 'g'), '');
				setDescription(newText);
				requestAnimationFrame(() => {
					if (textarea) textarea.focus();
				});
				return;
			}

			const suffix = description.length > 0 && !description.endsWith(' ') && !description.endsWith('\n') ? ' ' : '';
			const newText = `${description}${suffix}#${tag} `;
			setDescription(newText);

			requestAnimationFrame(() => {
				if (textarea) {
					textarea.focus();
					textarea.setSelectionRange(newText.length, newText.length);
				}
			});
		},
		[description],
	);

	/* ── Collect tags from description ── */
	const extractedTags = useMemo(() => {
		const tags: string[] = [];
		let match: RegExpExecArray | null = null;
		const regex = new RegExp(HASHTAG_REGEX.source, 'g');
		match = regex.exec(description);
		while (match !== null) {
			if (!tags.includes(match[1])) tags.push(match[1]);
			match = regex.exec(description);
		}
		return tags.slice(0, 5);
	}, [description]);

	/* ── Cover placeholder SVG ── */
	const COVER_PLACEHOLDER =
		"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='60' viewBox='0 0 400 60'%3E%3Crect fill='%23fafafa' width='400' height='60'/%3E%3Cg transform='translate(186,18)' fill='none' stroke='%23c8c8c8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='0' y='0' width='28' height='24' rx='2'/%3E%3Ccircle cx='8' cy='8' r='2.5' fill='%23c8c8c8'/%3E%3Cpath d='M2 24 L9 16 L14 20 L18 16 L26 24'/%3E%3C/g%3E%3C/svg%3E";

	/* ── Preview post for PostCard ── */
	const hasEdits = title.trim().length > 0 || description.trim().length > 0 || !!coverPreview;
	const previewPost = useMemo<CommunityPost>(
		() => ({
			id: 'preview',
			title: title || '标题预览',
			description: description || '正文预览...',
			coverImage: coverPreview || COVER_PLACEHOLDER,
			author: {
				id: currentUser?.userId ?? 'preview-user',
				displayName: currentUser?.nick || currentUser?.username || '匿名用户',
				avatar: currentUser?.avatar ?? '',
				bio: '',
				isVerified: false,
				creatorLevel: 1,
				joinedAt: new Date().toISOString(),
				stats: {
					posts: 0,
					followers: 0,
					following: 0,
					totalLikes: 0,
					totalCopies: 0,
					totalTips: 0,
				},
			},
			abilityType: 'discussion' as AbilityType,
			boardSlug: boardSlug || 'type-discussion',
			tags: extractedTags,
			stats: { likes: 0, comments: 0, copies: 0, favorites: 0, tips: 0 },
			isLiked: false,
			isFavorited: false,
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
		}),
		[title, description, coverPreview, boardSlug, currentUser, extractedTags],
	);

	/* ── Submit handler ── */
	const handleSubmit = useCallback(
		async (selectedBoardSlug?: string) => {
			if (!title.trim() || !description.trim()) return;
			setSubmitting(true);
			setSubmitError(null);
			try {
				let coverImageUrl = coverPreview ?? undefined;
				if (coverFile) {
					coverImageUrl = await uploadFile(coverFile);
				}

				const resolvedSlug = selectedBoardSlug ?? boardSlug;

				if (isEditMode) {
					await updatePost(editPost.id, {
						title: title.trim(),
						content: description.trim(),
						coverImage: coverImageUrl,
						boardSlug: resolvedSlug || undefined,
						tags: extractedTags,
					});
				} else {
					const draft: PublishDraft = {
						title: title.trim(),
						description: description.trim(),
						coverImage: coverImageUrl,
						abilityType: 'discussion' as AbilityType,
						boardSlug: resolvedSlug || 'type-discussion',
						tags: extractedTags,
						shareMode: 'showcase',
						aiMessage,
						skillIds,
					};
					const created = await publishPost(draft);
					setSubmittedPostId(created.id ?? null);
				}
				clearDraft();
				setSubmitted(true);
				analytics.track('CommunityPublish', {
					is_edit: isEditMode,
					board_slug: resolvedSlug || 'type-discussion',
					tags: extractedTags.join(','),
				});
				window.dispatchEvent(new CustomEvent('safeclaw:post-published'));
				if (isEditMode) {
					onSuccess?.();
					requestClose();
				}
			} catch (err) {
				const message = err instanceof Error ? err.message : '发布失败，请稍后重试';
				setSubmitError(message);
			} finally {
				setSubmitting(false);
			}
		},
		[title, description, boardSlug, extractedTags, coverPreview, coverFile, requestClose, isEditMode, editPost, onSuccess, aiMessage, skillIds],
	);

	/* ── Delete handler ── */
	const handleDeleteConfirm = useCallback(async () => {
		if (!editPost) return;
		setDeleting(true);
		try {
			await deletePost(editPost.id);
			setShowDeleteConfirm(false);
			onDeleted?.();
			requestClose();
		} catch {
			setSubmitError('删除失败，请稍后重试');
			setShowDeleteConfirm(false);
		} finally {
			setDeleting(false);
		}
	}, [editPost, requestClose, onDeleted]);

	/* ── Close / discard / save handlers ── */
	const handleClose = useCallback(() => {
		if (hasEdits && !submitted) {
			setShowExitConfirm(true);
		} else {
			requestClose();
		}
	}, [hasEdits, submitted, requestClose]);

	const handleSaveDraft = useCallback(() => {
		saveDraft({ title, description, coverPreview, boardSlug });
		toast.success('草稿已保存');
		setShowExitConfirm(false);
		requestClose();
	}, [title, description, coverPreview, boardSlug, toast, requestClose]);

	const handleDiscard = useCallback(() => {
		clearDraft();
		setShowExitConfirm(false);
		requestClose();
	}, [requestClose]);

	const handlePublishClick = useCallback(() => {
		setPickerSelectedBoard(boardSlug);
		setShowBoardPicker(true);
	}, [boardSlug]);

	const handleBoardPickerConfirm = useCallback(() => {
		if (!pickerSelectedBoard) return;
		setBoardSlug(pickerSelectedBoard);
		setShowBoardPicker(false);
		handleSubmit(pickerSelectedBoard);
	}, [pickerSelectedBoard, handleSubmit]);

	const canSubmit = title.trim().length > 0 && description.trim().length > 0;

	/* ── Form completeness for progress indicator ── */
	const formProgress = useMemo(() => {
		let filled = 0;
		if (title.trim().length > 0) filled++;
		if (description.trim().length > 0) filled++;
		if (boardSlug.length > 0) filled++;
		return filled;
	}, [title, description, boardSlug]);

	/* ── Dynamic tag row measurement ── */
	const [maxVisible, setMaxVisible] = useState(MOCK_TRENDING_TAGS.length);
	const tagsRowRef = useRef<HTMLDivElement>(null);
	const tagMeasureRef = useRef<HTMLDivElement>(null);
	const tagWidthsRef = useRef<number[]>([]);
	const measuredRef = useRef(false);

	useLayoutEffect(() => {
		if (!tagMeasureRef.current || !tagsRowRef.current) return;
		const buttons = tagMeasureRef.current.querySelectorAll<HTMLButtonElement>('[data-tag-btn]');
		if (buttons.length === 0) return;

		if (!measuredRef.current) {
			tagWidthsRef.current = Array.from(buttons, (btn) => btn.offsetWidth + 6);
			measuredRef.current = true;
		}

		const containerWidth = tagsRowRef.current.offsetWidth;
		const moreBtnWidth = 76;
		const available = containerWidth - moreBtnWidth;

		let used = 0;
		let count = 0;
		for (const w of tagWidthsRef.current) {
			if (used + w > available) break;
			used += w;
			count++;
		}
		if (count >= MOCK_TRENDING_TAGS.length) {
			setMaxVisible(MOCK_TRENDING_TAGS.length);
		} else {
			setMaxVisible(count);
		}
	}, [open]);

	const visibleTags = MOCK_TRENDING_TAGS.slice(0, maxVisible);
	const moreTags = maxVisible < MOCK_TRENDING_TAGS.length ? MOCK_TRENDING_TAGS.slice(maxVisible) : [];

	/* ── Close dropdowns on click outside ── */
	useEffect(() => {
		if (!showHashtagMenu && !showAllTags) return;
		const handler = (e: MouseEvent) => {
			if (showHashtagMenu && hashtagMenuRef.current && !hashtagMenuRef.current.contains(e.target as Node)) {
				setShowHashtagMenu(false);
			}
			if (showAllTags && tagsMoreRef.current && !tagsMoreRef.current.contains(e.target as Node) && !morePopupRef.current?.contains(e.target as Node)) {
				setShowAllTags(false);
				setMorePopupPos(null);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [showHashtagMenu, showAllTags]);

	const handleToggleShowAllTags = useCallback(() => {
		if (tagsMoreRef.current) {
			const rect = tagsMoreRef.current.getBoundingClientRect();
			setMorePopupPos({ top: rect.bottom + 4, left: Math.max(rect.right - 200, 8) });
		}
		setShowAllTags((v) => !v);
	}, []);
	if (!open) return null;

	/* ── Transition ── */
	const overlayClass = closing ? 'opacity-0 backdrop-blur-none' : entered ? 'opacity-100 backdrop-blur-xl' : 'opacity-0 backdrop-blur-none';

	const panelStyle: React.CSSProperties = {
		opacity: closing ? 0 : entered ? 1 : 0,
		transform: closing ? 'scale(0.96) translateY(8px)' : entered ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(12px)',
		filter: closing ? 'blur(4px)' : entered ? 'blur(0)' : 'blur(6px)',
		transitionTimingFunction: closing ? 'cubic-bezier(0.4, 0, 1, 1)' : 'cubic-bezier(0.16, 1, 0.3, 1)',
	};

	const _selectedBoard = COMMUNITY_BOARDS.find((b) => b.slug === boardSlug);

	return createPortal(
		<>
			{/* Backdrop */}
			<div
				aria-hidden
				className={`liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out ${overlayClass}`}
				onClick={handleClose}
				style={{ zIndex: Z.MODAL_OVERLAY, pointerEvents: closing || !entered ? 'none' : 'auto' }}
			/>

			{/* Centered modal */}
			<div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_CONTENT, pointerEvents: 'none' }}>
				{/* Dual-panel flex container */}
				<div className="flex flex-row items-stretch transition-all duration-300" style={{ ...panelStyle, pointerEvents: 'auto', gap: 12 }}>
					{/* ── Left panel: preview ── */}
					<PublishDetailPreview hasRealCover={!!coverPreview} post={previewPost} />

					{/* ── Right panel: form card ── */}
					<div
						className="liquid-glass-panel flex-1 min-w-[640px] max-h-[90vh] overflow-hidden rounded-2xl flex flex-col transition-all duration-300 shadow-sm"
						style={{ background: 'white' }}
					>
						{/* ═══════ Progress bar ═══════ */}
						{!submitted && (
							<div className="h-[2px] w-full bg-border/10 shrink-0 overflow-hidden">
								<div className="h-full bg-primary transition-all duration-500 ease-out" style={{ width: `${(formProgress / 3) * 100}%` }} />
							</div>
						)}

						{/* ═══════ Header ═══════ */}
						<div className="flex items-center justify-between px-5 py-3 shrink-0 border-b border-border/15">
							<div className="flex items-center gap-2">
								<div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10">
									<PenLine className="h-3.5 w-3.5 text-primary" />
								</div>
								<h2 className="text-sm font-semibold text-foreground">{isEditMode ? '编辑笔记' : '发布笔记'}</h2>
							</div>
							<button
								className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-foreground/5 text-muted-foreground hover:text-foreground transition-colors"
								onClick={handleClose}
								type="button"
							>
								<X className="h-4 w-4" />
							</button>
						</div>

						{submitted ? (
							/* ═══════ Success state ═══════ */
							<div className="flex-1 flex flex-col items-center justify-center gap-4 py-16 relative overflow-hidden">
								{/* Decorative circles */}
								<div className="absolute inset-0 pointer-events-none">
									<div className="absolute top-1/4 left-1/4 w-24 h-24 rounded-full bg-primary/5 animate-ping" style={{ animationDuration: '2s' }} />
									<div
										className="absolute bottom-1/3 right-1/4 w-16 h-16 rounded-full bg-primary/5 animate-ping"
										style={{ animationDuration: '2.5s', animationDelay: '0.3s' }}
									/>
								</div>
								<div className="relative">
									<div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
										<Check className="h-8 w-8 text-primary-foreground" strokeWidth={3} />
									</div>
								</div>
								<div className="text-center relative">
									<p className="text-sm font-bold text-foreground">{isEditMode ? '修改成功！' : '发布成功！'}</p>
									<p className="text-xs text-muted-foreground/60 mt-1.5">{isEditMode ? '你的修改已保存' : '你的内容已分享到社区，其他用户可以学习你的使用方法'}</p>
								</div>
								{!isEditMode && (
									<div className="flex items-center gap-3 mt-2 relative">
										{submittedPostId && (
											<button
												className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
												onClick={() => {
													const url = `/community/post/${submittedPostId}`;
													window.open(url, '_blank');
												}}
												type="button"
											>
												跳转查看
											</button>
										)}
										<button
											className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
											onClick={() => {
												onSuccess?.();
												requestClose();
											}}
											type="button"
										>
											关闭
										</button>
									</div>
								)}
							</div>
						) : (
							/* ═══════ Form body ═══════ */
							<PublishFormPanel
								coverError={coverError}
								coverInputRef={coverInputRef}
								coverPreview={coverPreview}
								coverShrinking={coverShrinking}
								description={description}
								filteredSuggestions={filteredSuggestions}
								hashtagMenuIndex={hashtagMenuIndex}
								hashtagMenuPosition={hashtagMenuPosition}
								hashtagMenuRef={hashtagMenuRef}
								morePopupPos={morePopupPos}
								morePopupRef={morePopupRef}
								moreTags={moreTags}
								onCoverInputChange={handleCoverInputChange}
								onCoverRemove={handleCoverRemove}
								onDescriptionChange={handleDescriptionChange}
								onInsertHashtag={insertHashtag}
								onTextareaKeyDown={handleTextareaKeyDown}
								onTitleChange={setTitle}
								onToggleShowAllTags={handleToggleShowAllTags}
								onTrendingTagClick={handleTrendingTagClick}
								showAllTags={showAllTags}
								showHashtagMenu={showHashtagMenu}
								submitError={submitError}
								tagMeasureRef={tagMeasureRef}
								tagsMoreRef={tagsMoreRef}
								tagsRowRef={tagsRowRef}
								textareaRef={textareaRef}
								title={title}
								visibleTags={visibleTags}
							/>
						)}

						{/* ═══════ Footer ═══════ */}
						{!submitted && (
							<div className="border-t border-border/15 px-5 py-2.5 flex items-center justify-end gap-2 shrink-0">
								{/* Delete button (edit mode only) */}
								{isEditMode && (
									<button
										className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-colors mr-auto"
										onClick={() => setShowDeleteConfirm(true)}
										type="button"
									>
										<Trash2 className="h-3.5 w-3.5" />
										<span>删除</span>
									</button>
								)}

								{/* Publish button */}
								<button
									className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.97]"
									disabled={!canSubmit || submitting}
									onClick={handlePublishClick}
									type="button"
								>
									{submitting ? (
										<>
											<div className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
											发布中...
										</>
									) : (
										<>
											<Upload className="h-3.5 w-3.5" />
											{isEditMode ? '保存' : '发布'}
										</>
									)}
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* ═══════ Cover crop modal (layered on top) ═══════ */}
			{rawCoverSrc && <CoverCropModal imageSrc={rawCoverSrc} onCancel={handleCropCancel} onConfirm={handleCropConfirm} />}

			{/* ═══════ Compress progress modal ═══════ */}
			{compressing && <CompressProgressModal onCancel={cancelCompress} progress={compressProgress} />}

			{/* ═══════ Delete confirmation dialog ═══════ */}
			{showDeleteConfirm && (
				<div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_CONTENT + 10 }}>
					<div aria-hidden className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !deleting && setShowDeleteConfirm(false)} />
					<div className="relative liquid-glass-panel rounded-2xl p-6 max-w-sm w-full space-y-4">
						<div className="flex items-center gap-2.5">
							<div className="flex items-center justify-center w-9 h-9 rounded-full bg-red-500/10">
								<Trash2 className="h-4 w-4 text-red-500" />
							</div>
							<h3 className="text-sm font-semibold text-foreground">确认删除</h3>
						</div>
						<p className="text-xs text-muted-foreground leading-relaxed">确定要删除「{editPost?.title}」吗？此操作无法撤销。</p>
						<div className="flex justify-end gap-2 pt-1">
							<button
								className="px-4 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors disabled:opacity-50"
								disabled={deleting}
								onClick={() => setShowDeleteConfirm(false)}
								type="button"
							>
								取消
							</button>
							<button
								className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 active:scale-[0.97]"
								disabled={deleting}
								onClick={handleDeleteConfirm}
								type="button"
							>
								{deleting ? (
									<>
										<div className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
										删除中...
									</>
								) : (
									'确认删除'
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* ═══════ Board picker modal ═══════ */}
			{showBoardPicker && (
				<div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_CONTENT + 10 }}>
					<div aria-hidden className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowBoardPicker(false)} />
					<div className="relative liquid-glass-panel rounded-2xl p-6 max-w-md w-full space-y-4">
						<div className="flex items-center justify-between">
							<h3 className="text-sm font-semibold text-foreground">主题板块</h3>
							<button
								className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-foreground/5 text-muted-foreground hover:text-foreground transition-colors"
								onClick={() => setShowBoardPicker(false)}
								type="button"
							>
								<X className="h-4 w-4" />
							</button>
						</div>

						<div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
							{COMMUNITY_BOARDS.filter((b) => b.category === 'scenario').map((board) => (
								<button
									className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-xs transition-all ${pickerSelectedBoard === board.slug
										? 'bg-primary/10 text-primary border border-primary/20 ring-1 ring-primary/10'
										: 'bg-foreground/[0.03] text-foreground/80 hover:bg-foreground/[0.06] border border-transparent'
										}`}
									key={board.slug}
									onClick={() => setPickerSelectedBoard(board.slug)}
									type="button"
								>
									<span className="truncate">{board.name}</span>
								</button>
							))}
						</div>

						<div className="flex justify-end gap-2 pt-1">
							<button
								className="px-4 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
								onClick={() => setShowBoardPicker(false)}
								type="button"
							>
								取消
							</button>
							<button
								className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97]"
								disabled={!pickerSelectedBoard || submitting}
								onClick={handleBoardPickerConfirm}
								type="button"
							>
								{submitting ? (
									<>
										<div className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
										发布中...
									</>
								) : (
									<>
										<Upload className="h-3.5 w-3.5" />
										确认发布
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* ═══════ Exit confirmation dialog ═══════ */}
			{showExitConfirm && (
				<div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_CONTENT + 10 }}>
					<div aria-hidden className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowExitConfirm(false)} />
					<div className="relative liquid-glass-panel rounded-2xl p-6 max-w-sm w-full space-y-4">
						<div className="flex items-center gap-2.5">
							<div className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-500/10">
								<AlertCircle className="h-4 w-4 text-amber-500" />
							</div>
							<h3 className="text-sm font-semibold text-foreground">退出编辑</h3>
						</div>
						<p className="text-xs text-muted-foreground leading-relaxed">你有未保存的内容，是否保存为草稿？</p>
						<div className="flex justify-end gap-2 pt-1">
							<button
								className="px-4 py-2 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
								onClick={handleDiscard}
								type="button"
							>
								放弃修改
							</button>
							<button
								className="px-4 py-2 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors active:scale-[0.97]"
								onClick={handleSaveDraft}
								type="button"
							>
								保存草稿
							</button>
						</div>
					</div>
				</div>
			)}
		</>,
		document.body,
	);
}
